import {Box, DesktopOnly, Layout, MobileOnly, styled, Text, useTheme} from '@layerzerolabs/ui-kit';
import {Stack} from '@mui/system';
import {kebabCase} from 'lodash';
import {useRouter} from 'next/router';
import React from 'react';

import {AppFooter, AppHeader} from '../components/Layout';

const ScrollContainer = styled(Stack)(({theme}) => ({
  overflowY: 'scroll',

  display: 'flex',
  flexDirection: 'column',
  '&::-webkit-scrollbar': {
    width: '1px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.text.secondary,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.text.secondary,
  },
}));

type HeadingLinkProps = {
  heading: Heading;
  isActive?: boolean;
};

const HeadingLink: React.FC<HeadingLinkProps> = ({isActive, heading}) => {
  const theme = useTheme();
  const router = useRouter();
  return (
    <Text
      onClick={() => router.push(`/faq#${heading.id}`)}
      color={isActive ? theme.palette.text.primary : theme.palette.text.secondary}
      sx={{
        cursor: 'pointer',
        '&:hover': {color: theme.palette.text.primary},
        mb: 1.5,
      }}
    >
      {String(heading.paddedIndex).padStart(2, '0')} {heading.title}
    </Text>
  );
};

type Heading = {title: string; paddedIndex: number; id: string; children: React.ReactNode};
function createHeading(
  title: string,
  paddedIndex: number,
  id: string = title,
  children?: React.ReactElement,
): Heading {
  return {title, paddedIndex, id, children};
}

const HEADINGS = [
  {
    title: 'Intro',
    content: (
      <>
        Welcome to Stargate’s AptosBridge, built on the LayerZero protocol. The Aptos blockchain is
        built on a fundamentally different architecture relative to other chains and has unique
        transfer and registration requirements which are focused on user security. Users are
        required to have both an EVM and an Aptos-compatible wallet to successfully transfer assets
        using the bridge.
      </>
    ),
  },
  {
    title: 'Bridge Functionality',
    content: (
      <>
        The AptosBridge is a wrapped-asset bridge where users can bridge assets from any of the
        supported EVM chains into Aptos and receive unified liquidity in the following supported
        assets: USDC, USDT, WETH, WBTC and USDD. BTC.b and CAKE are also available OFTs (Omnichain
        Fungible Tokens)
        <Box sx={{mb: 2}}></Box>
        The AptosBridge previously had transfer limits, though these have been removed following a
        vote within StargateDAO.
      </>
    ),
  },
  {
    title: 'Registering & Claiming Assets',
    content: (
      <>
        The AptosBridge now comes with built-in implicit coin registration meaning that most users
        can bridge into Aptos freely. If you have opted for explicit coin registration, you will
        need to claim your assets. You should be prompted to do so.
      </>
    ),
  },
  {
    title: 'Transferring Assets from Aptos to Other Chains',
    content: (
      <>
        Transferring assets from Aptos back to EVM chains does not require additional claim
        transactions. These transactions used to take up to 3 days, but now happen in just a couple
        minutes. While bridging into Aptos is free, the StargateDAO has placed a 7bps fee on
        transactions leaving Aptos.
      </>
    ),
  },
].map(({title, content}, index) => {
  const paddedIndex = index + 1;
  return createHeading(
    title,
    paddedIndex,
    kebabCase(title),
    <>
      <Text variant='p2' sx={{display: 'block'}} color='text.secondary'>
        {content}
      </Text>
    </>,
  );
});

function useActiveHeading(headings: Heading[]) {
  const hash = useRouter().asPath.split('#')[1];
  const activeHeading = headings.find((h) => h.id === hash) ?? HEADINGS[0];
  return activeHeading;
}

const Faq = () => {
  const activeHeading = useActiveHeading(HEADINGS);
  return (
    <Layout header={<AppHeader />} footer={<AppFooter />}>
      <DesktopOnly
        sx={{
          width: '1116px',
          margin: '0 auto',
          mt: 8,
        }}
      >
        <Stack direction='row' alignItems='stretch' justifyContent='space-between'>
          <Stack sx={{width: '460px'}}>
            <Text variant='h1' sx={{mb: 7, fontSize: '36px', lineHeight: '96%'}}>
              Frequently Asked Questions
            </Text>
            {HEADINGS.map((heading) => (
              <HeadingLink
                key={heading.id}
                isActive={activeHeading === heading}
                heading={heading}
              />
            ))}
          </Stack>
          <ScrollContainer
            sx={{width: '546px', height: 'calc(100vh - 186px)', overflow: 'auto', pr: 3}}
          >
            {HEADINGS.map((h) => (
              <React.Fragment key={h.id}>
                <Text variant='p2' color='text.secondary' id={h.id}>
                  {String(h.paddedIndex).padStart(2, '0')}
                </Text>
                <Text variant='p1' sx={{marginY: 1, fontSize: 18}}>
                  {h.title}
                </Text>
                {h.children}
                <Box sx={{mb: 5}}></Box>
              </React.Fragment>
            ))}
          </ScrollContainer>
        </Stack>
      </DesktopOnly>

      <MobileOnly>
        <Stack
          sx={{
            mt: 5,
            paddingX: 2,
          }}
          direction='column'
        >
          <Stack sx={{mb: 5}}>
            <Text
              variant='h1'
              sx={{
                fontSize: '24px',
                lineHeight: '96%',
                letterSpacing: '-0.06em',
                maxWidth: '210px',
              }}
            >
              Frequently Asked Questions
            </Text>
            <Box my={3}>
              <img src='/static/page-spacer.svg' />
            </Box>
            {HEADINGS.map((heading) => (
              <HeadingLink
                key={heading.id}
                isActive={activeHeading === heading}
                heading={heading}
              />
            ))}
          </Stack>

          {HEADINGS.map((h) => (
            <React.Fragment key={h.id}>
              <Text variant='p2' color='text.secondary' id={h.id}>
                {String(h.paddedIndex).padStart(2, '0')}
              </Text>
              <Text variant='p1' sx={{marginY: 1, fontSize: 18}}>
                {h.title}
              </Text>
              {h.children}
              <Box sx={{mb: 5}}></Box>
            </React.Fragment>
          ))}
        </Stack>
      </MobileOnly>
    </Layout>
  );
};

export default Faq;
